import React from "react";
import { Document, Page, Text, View, StyleSheet, Image } from "@react-pdf/renderer";
import { getCodeColor } from "../components";



// Helper function to format a description with bold keys
const formatDescription = (description) => {
	if (!description) return "";

	if (typeof description === "object") {
		let parts = [];
		Object.entries(description).forEach(([key, value], index) => {
			parts.push(
				<React.Fragment key={index}>
					<Text style={{ fontFamily: "Helvetica-Bold" }}>{key.toUpperCase()}: </Text>
					<Text>{value}</Text>
					{index < Object.keys(description).length - 1 && <Text>{"\n"}</Text>}
				</React.Fragment>
			);
		});
		return <>{parts}</>;
	}

	return description;
};

const GenerateNotaTraslado = ({ notaTraslado, themeColor }) => {
	const themeConverted = getCodeColor(themeColor);

	const styles = StyleSheet.create({
		page: {
			flexDirection: "column",
			padding: 10,
			fontSize: 10,
			fontFamily: "Helvetica",
			position: "relative",
			bottom: "30",
		},
		headerContainer: {
			flexDirection: "row",
			alignContent: "center",
			paddingBottom: 10,
		},
		headerLeft: {
			flexDirection: "column",
			width: "40%",
			lineHeight: 1.2,
			fontSize: 7,
		},
		logo: {
			width: 150,
			height: 100,
		},
		titleBig: {
			fontFamily: "Helvetica-Bold",
			fontSize: 19,
			letterSpacing: 1,
			margin: 0,
			paddingLeft: 35,
			position: "relative",
			top: "15",
			right: "40",
		},
		titleNumber: {
			fontSize: 18,
			color: "#ff3d3d",
			fontWeight: "bold",
			paddingLeft: 70,
			letterSpacing: 2,
			fontFamily: "Helvetica-Bold",
			position: "relative",
			left: 0,
		},
		subHeaderContainer: {
			flexDirection: "row",
			justifyContent: "space-between",
			paddingRight: "10",
			alignItems: "center",
			position: "relative",
			bottom: 5,
			fontSize: 9,
		},
		tableContainer: {
			width: "100%",
			marginVertical: 10,
			position: "relative",
			bottom: 28,
		},
		tableHeader: {
			flexDirection: "row",
			backgroundColor: themeConverted,
			color: "#fff",
			fontWeight: "bold",
		},
		tableHeaderCell: {
			padding: 5,
			textAlign: "center",
			fontWeight: "bold",
			fontSize: 8,
			color: "white",
		},
		tableRow: {
			flexDirection: "row",
		},
		tableCell: {
			padding: 5,
			textAlign: "center",
			fontSize: 8,
		},
		tableCellNro: { flex: 0.5 },
		tableCellCodigo: { flex: 1.5 },
		tableCellDescripcion: { flex: 4, textAlign: "left" },
		tableCellCantidad: { flex: 0.8 },
		tableCellPrecioUnitario: { flex: 1.2 },
		tableCellPrecio: { flex: 1.2 },
		bottomTotalsRow: {
			flexDirection: "row",
			alignItems: "center",
			justifyContent: "space-between",
			marginTop: 0,
			position: "relative",
			bottom: 35,
		},
		bottomTotalsTextContainer: {
			padding: 7,
		},
		bottomTotalsText: {
			fontSize: 8,
			fontWeight: "bold",
			textAlign: "left",
		},
		bottomTotalsAmount: {
			borderRadius: 5,
			justifyContent: "center",
		},
		bottomTotalsAmountText: {
			fontSize: 8,
			fontWeight: "bold",
		},
		signatureContainer: {
			display: "flex",
			flexDirection: "row",
			justifyContent: "space-around",
			marginTop: 20,
		},
		signatureLine: {
			marginBottom: 5,
		},
		signatureBox: {
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
		},
		pageNumbers: {
			position: "absolute",
			bottom: 20,
			right: 20,
			textAlign: "right",
		},
	});
	// Default empty data if nothing is passed
	const data = notaTraslado || {
		nro: 0,
		oDate: "",
		oTime: "",
		byBranch_name: "",
		forBranch_name: "",
		sender_name: "",
		logo: "",
		total: "0.00",
		body_table: [],
	};

	// Calculate total items
	const totalItems = data.body_table.reduce((acc, item) => acc + (parseInt(item.qp) || 0), 0);

	// Number to words function (simplified)
	const numberToWords = (amount) => {
		// This would be replaced with a proper number-to-words function
		// For now, returning a placeholder
		return `${amount} 00/100 Bolivianos`;
	};
	const apiUrl = process.env.REACT_APP_API_URL;
	return (
		<Document>
			<Page size="A4" style={styles.page}>
				{/* Header */}
				<View style={styles.headerContainer}>
					<View style={styles.headerLeft}>
						<View style={{ position: "relative", top: 7 }}>
							<Image src={`${apiUrl}${data.logo}`} style={styles.logo} />
						</View>
					</View>
					<View style={{ justifyContent: "center", alignItems: "center" }}>
						<Text style={styles.titleBig}>Nota de Traslado</Text>
					</View>
					<View style={{ justifyContent: "center", alignItems: "center" }}>
						<Text style={styles.titleNumber}>N°{data.nro}</Text>
					</View>
				</View>

				{/* Sub-header */}
				<View
					style={{
						width: "100%",
						display: "flex",
						flexDirection: "column",
						alignItems: "flex-end",
						gap: 5,
						position: "relative",
						bottom: 20,
					}}>
					<View style={{ display: "flex", flexDirection: "row", gap: "5", position: "relative", bottom: 0 }}>
						<Text>Fecha: {data.oDate}</Text>
						<Text style={{ fontSize: 7, position: "relative", top: "1.2" }}>{data.oTime}</Text>
					</View>
					<View style={{ display: "flex", flexDirection: "row", gap: 5 }}>
						<Text>Traslado de {data.byBranch_name}</Text>
						<Text>a: {data.forBranch_name}</Text>
					</View>
				</View>

				{/* Table */}
				<View style={styles.tableContainer}>
					{/* Table Header */}
					<View style={styles.tableHeader}>
						<Text style={[styles.tableHeaderCell, styles.tableCellNro]}>Nro</Text>
						<Text style={[styles.tableHeaderCell, styles.tableCellCodigo]}>Código</Text>
						<Text style={[styles.tableHeaderCell, styles.tableCellDescripcion]}>Descripción</Text>
						<Text style={[styles.tableHeaderCell, styles.tableCellCantidad]}>Cantidad</Text>
						<Text style={[styles.tableHeaderCell, styles.tableCellPrecioUnitario]}>Precio Unitario</Text>
						<Text style={[styles.tableHeaderCell, styles.tableCellPrecio]}>Precio</Text>
					</View>

					{/* Table Rows */}
					{data.body_table.map((item, index) => (
						<View style={styles.tableRow} key={index}>
							<Text style={[styles.tableCell, styles.tableCellNro]}>{index + 1}</Text>
							<Text style={[styles.tableCell, styles.tableCellCodigo]}>{item.product_code}</Text>
							<View style={[styles.tableCell, styles.tableCellDescripcion]}>
								{typeof item.product_name === "object" ? (
									formatDescription(item.product_name)
								) : (
									<Text>{item.product_name}</Text>
								)}
							</View>
							<Text style={[styles.tableCell, styles.tableCellCantidad]}>{item.qp}</Text>
							<Text style={[styles.tableCell, styles.tableCellPrecioUnitario]}>{item.price}</Text>
							<Text style={[styles.tableCell, styles.tableCellPrecio]}>{item.sub_total}</Text>
						</View>
					))}
				</View>

				{/* Totals */}
				<View style={[styles.bottomTotalsRow]}>
					<View
						style={[styles.bottomTotalsTextContainer, { flex: 4, backgroundColor: themeConverted, padding: 5 }]}>
						<Text style={[styles.bottomTotalsText, { color: "#fff" }]}>
							Items: {totalItems} Valor total de: {numberToWords(data.total)}
						</Text>
					</View>
					<View
						style={[
							styles.bottomTotalsAmount,
							{ flex: 1, backgroundColor: "#fff", padding: 5, marginLeft: 5 },
						]}>
						<Text
							style={[
								styles.bottomTotalsAmountText,
								{ color: "#000", fontWeight: "bold", textAlign: "right", letterSpacing: "0.5" },
							]}>
							Total: Bs.{parseFloat(data.total).toFixed(2)}
						</Text>
					</View>
				</View>

				{/* Signatures */}
				<View style={styles.signatureContainer}>
					<View style={styles.signatureBox}>
						<Text style={styles.signatureLine}>___________________________</Text>
						<Text>{data.sender_name}</Text>
						<Text>Remitente</Text>
					</View>
					<View style={styles.signatureBox}>
						<Text style={styles.signatureLine}>___________________________</Text>
						<Text>Recibido</Text>
					</View>
				</View>
				<Text
					style={styles.pageNumbers}
					render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
					fixed
				/>
			</Page>
		</Document>
	);
};

export default GenerateNotaTraslado;
